import { AltLabel } from '../../TreeViewTypes';
import { DataNode, LabelContext } from '../../data_nodes/types';
import { LabelGenerator, DefaultLabelGenerator } from '../types';

/**
 * Generate default label for Additional Information nodes
 */
export const generateAdditionalInfoDefaultLabel: DefaultLabelGenerator = (
  dataNode: DataNode<any>,
  context: LabelContext,
): string => {
  const infoData = dataNode.data;

  // Handle string data directly (most common case)
  if (typeof infoData === 'string') {
    return infoData;
  }

  // Handle object with text property
  if (infoData && typeof infoData === 'object' && 'text' in infoData) {
    return infoData.text as string;
  }

  // Handle object with additionalInfo property
  if (infoData && typeof infoData === 'object' && 'additionalInfo' in infoData) {
    return infoData.additionalInfo as string;
  }

  return 'Additional Information';
};

/**
 * Generate alt labels for Additional Information nodes
 *
 * Provides options for full text vs truncated display
 */
export const generateAdditionalInfoAltLabels: LabelGenerator = (
  dataNode: DataNode<any>,
  context: LabelContext,
): AltLabel[] => {
  const fullText = generateAdditionalInfoDefaultLabel(dataNode, context);

  const labels: AltLabel[] = [
    {
      key: 'default',
      selectOption: 'Default: Full text',
      value: fullText,
    },
  ];

  // Add truncated option if text is long
  if (fullText.length > 50) {
    const truncated = fullText.length > 47 ? fullText.substring(0, 47) + '...' : fullText;

    labels.push({
      key: 'truncated',
      selectOption: 'Truncated: First 50 characters',
      value: truncated,
    });
  }

  // Add word count option if text is substantial
  if (fullText.length > 20) {
    const wordCount = fullText.split(/\s+/).length;
    labels.push({
      key: 'summary',
      selectOption: 'Summary: Word count',
      value: `Additional info (${wordCount} words)`,
    });
  }

  return labels;
};
